import React, { useState, useEffect } from 'react';
import { playSound } from '../lib/soundEffects';
import { startVoiceDictation, stopVoiceDictation } from '../lib/voiceDictation';

const STORAGE_KEY = 'memark_scratchpad_markdown';

const DEFAULT_NOTE = `# 📝 Sovereign Research Scratchpad

Welcome to your offline, encrypted markdown scratchpad.

- [x] Review today's AI research bookmarks
- [ ] Group cloud infrastructure bookmarks into folders
- [ ] Export Obsidian vault sync bundle

---

### Quick References & Key Links
Press the button below to automatically insert your current bookmarks directly into this document!
`;

export default function ScratchpadDrawer({ isOpen, onClose, currentBookmarks = [], isDark = true }) {
  const [content, setContent] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved !== null ? saved : DEFAULT_NOTE;
    } catch {
      return DEFAULT_NOTE;
    }
  });

  const [activeTab, setActiveTab] = useState('split'); // 'edit', 'preview', 'split'
  const [copied, setCopied] = useState(false);
  const [isDictating, setIsDictating] = useState(false);

  useEffect(() => {
    return () => {
      stopVoiceDictation();
    };
  }, []);

  const toggleDictation = () => {
    if (isDictating) {
      stopVoiceDictation();
      setIsDictating(false);
      playSound('blip');
    } else {
      playSound('launch');
      const rec = startVoiceDictation({
        onResult: ({ final }) => {
          if (final) {
            setContent((prev) => (prev ? prev.trim() + ' ' + final : final));
          }
        },
        onError: () => setIsDictating(false),
        onEnd: () => setIsDictating(false),
      });
      if (rec) setIsDictating(true);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, content);
    } catch (e) {
      console.warn('Failed to save scratchpad', e);
    }
  }, [content]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        stopVoiceDictation();
        setIsDictating(false);
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleInsertBookmarks = () => {
    playSound('launch');
    if (!currentBookmarks || currentBookmarks.length === 0) return;

    let appendText = `\n\n### 🔗 Inserted Bookmarks (${new Date().toLocaleTimeString()})\n`;
    currentBookmarks.forEach(bm => {
      const tags = (bm.tags || []).map(t => t.startsWith('#') ? t : `#${t}`).join(' ');
      const clicks = bm.clicks ? ` (⚡ ${bm.clicks} hits)` : '';
      appendText += `- [${bm.title || 'Untitled'}](${bm.url || '#'}) ${tags}${clicks}\n`;
    });

    setContent(prev => prev + appendText);
  };

  const handleCopy = () => {
    playSound('blip');
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    playSound('launch');
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MeMark-Scratchpad-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    if (window.confirm('Clear all scratchpad contents?')) {
      playSound('blip');
      setContent('');
    }
  };

  // Simple, robust client-side markdown renderer for the preview pane
  const renderSimpleMarkdown = (text) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      if (line.startsWith('# ')) {
        return <h1 key={idx} className={`text-xl font-bold mt-4 mb-2 pb-1 border-b border-emerald-500/20 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={idx} className={`text-lg font-semibold mt-3 mb-1 ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>{line.slice(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={idx} className={`text-sm font-semibold mt-2 mb-1 ${isDark ? 'text-cyan-300' : 'text-teal-700'}`}>{line.slice(4)}</h3>;
      }
      if (line.startsWith('- [x] ') || line.startsWith('- [X] ')) {
        return (
          <div key={idx} className={`flex items-center gap-2 text-xs my-1 ${isDark ? 'text-emerald-400/80' : 'text-emerald-700/80'}`}>
            <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>☑</span>
            <span className="line-through">{line.slice(6)}</span>
          </div>
        );
      }
      if (line.startsWith('- [ ] ')) {
        return (
          <div key={idx} className={`flex items-center gap-2 text-xs my-1 ${isDark ? 'text-neutral-300' : 'text-slate-700'}`}>
            <span className={isDark ? 'text-neutral-500' : 'text-slate-400'}>☐</span>
            <span>{line.slice(6)}</span>
          </div>
        );
      }
      if (line.startsWith('- ')) {
        // Link match: [title](url)
        const linkMatch = line.slice(2).match(/\[(.*?)\]\((.*?)\)(.*)/);
        if (linkMatch) {
          return (
            <div key={idx} className={`text-xs ml-3 my-0.5 list-disc flex items-center gap-1 ${isDark ? 'text-neutral-300' : 'text-slate-700'}`}>
              <span className="text-emerald-500">•</span>
              <a href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className={`${isDark ? 'text-cyan-400' : 'text-teal-600'} hover:underline font-medium`}>
                {linkMatch[1]}
              </a>
              <span className={isDark ? 'text-neutral-400' : 'text-slate-500'}>{linkMatch[3]}</span>
            </div>
          );
        }
        return <div key={idx} className={`text-xs ml-3 my-0.5 ${isDark ? 'text-neutral-300' : 'text-slate-700'}`}>• {line.slice(2)}</div>;
      }
      if (line.startsWith('> ')) {
        return (
          <blockquote key={idx} className={`border-l-2 border-emerald-500/40 pl-3 italic text-xs my-2 ${isDark ? 'text-neutral-400' : 'text-slate-500'}`}>
            {line.slice(2)}
          </blockquote>
        );
      }
      if (line.trim() === '---') {
        return <hr key={idx} className={`my-3 ${isDark ? 'border-neutral-800' : 'border-slate-200'}`} />;
      }
      if (!line.trim()) {
        return <div key={idx} className="h-2" />;
      }
      return <p key={idx} className={`text-xs my-1 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-slate-800'}`}>{line}</p>;
    });
  };

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const charCount = content.length;

  return (
    <div className={`fixed inset-y-0 right-0 z-50 w-full max-w-2xl backdrop-blur-xl border-l border-emerald-500/30 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 ${
      isDark ? 'bg-neutral-950/95 text-white' : 'bg-white/95 text-slate-900'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-5 py-4 border-b ${
        isDark ? 'border-neutral-800/80 bg-neutral-900/50' : 'border-slate-200 bg-slate-50'
      }`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 text-lg">
            📝
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className={`text-sm font-bold tracking-wide ${isDark ? 'text-white' : 'text-slate-900'}`}>SOVEREIGN SCRATCHPAD</h2>
              <span className="text-[10px] bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 px-2 py-0.5 rounded font-mono font-semibold">
                LOCAL ENCLAVE
              </span>
            </div>
            <p className={`text-[11px] font-mono ${isDark ? 'text-neutral-400' : 'text-slate-500'}`}>
              {wordCount} words • {charCount} chars • Auto-persisted
            </p>
          </div>
        </div>

        {/* View mode toggle */}
        <div className={`flex items-center gap-1 p-1 rounded-lg border ${
          isDark ? 'bg-neutral-900 border-neutral-800' : 'bg-slate-100 border-slate-200'
        }`}>
          <button
            onClick={() => setActiveTab('edit')}
            className={`px-2.5 py-1 text-xs rounded font-mono transition-all cursor-pointer ${
              activeTab === 'edit'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-semibold'
                : isDark ? 'text-neutral-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Raw
          </button>
          <button
            onClick={() => setActiveTab('split')}
            className={`px-2.5 py-1 text-xs rounded font-mono transition-all cursor-pointer ${
              activeTab === 'split'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-semibold'
                : isDark ? 'text-neutral-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Split
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-2.5 py-1 text-xs rounded font-mono transition-all cursor-pointer ${
              activeTab === 'preview'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-semibold'
                : isDark ? 'text-neutral-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Preview
          </button>
        </div>

        {/* Close Button */}
        <button
          onClick={() => {
            playSound('blip');
            onClose();
          }}
          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
            isDark ? 'text-neutral-400 hover:text-white hover:bg-neutral-800/80' : 'text-slate-400 hover:text-slate-800 hover:bg-slate-200'
          }`}
          title="Close (Esc)"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Action Toolbar */}
      <div className={`px-5 py-2.5 border-b flex flex-wrap items-center justify-between gap-2 ${
        isDark ? 'bg-neutral-900/30 border-neutral-800/80' : 'bg-slate-50 border-slate-200'
      }`}>
        <button
          onClick={handleInsertBookmarks}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all shadow-sm cursor-pointer ${
            isDark
              ? 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
              : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300'
          }`}
          title="Embed all visible bookmarks as markdown links"
        >
          <span>🔗</span>
          <span>+ Embed Current Bookmarks ({currentBookmarks.length})</span>
        </button>

        <div className="flex items-center gap-1.5">
          <button
            onClick={toggleDictation}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-mono transition-all border flex items-center gap-1.5 cursor-pointer ${
              isDictating
                ? 'bg-rose-500/20 text-rose-300 border-rose-500/50 animate-pulse'
                : isDark
                ? 'bg-neutral-800/80 hover:bg-neutral-800 text-neutral-300 hover:text-white border-neutral-700/60'
                : 'bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 border-slate-200 shadow-xs'
            }`}
            title="Continuous hands-free voice dictation"
          >
            <span>🎙️</span>
            <span>{isDictating ? 'Listening...' : 'Dictate'}</span>
          </button>

          <button
            onClick={handleCopy}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-mono transition-colors border cursor-pointer ${
              isDark
                ? 'bg-neutral-800/80 hover:bg-neutral-800 text-neutral-300 hover:text-white border-neutral-700/60'
                : 'bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 border-slate-200 shadow-xs'
            }`}
            title="Copy Markdown to Clipboard"
          >
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>

          <button
            onClick={handleDownload}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-mono transition-colors border cursor-pointer ${
              isDark
                ? 'bg-neutral-800/80 hover:bg-neutral-800 text-neutral-300 hover:text-white border-neutral-700/60'
                : 'bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 border-slate-200 shadow-xs'
            }`}
            title="Download .md file"
          >
            ⬇️ Save .md
          </button>

          <button
            onClick={handleClear}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-mono transition-colors cursor-pointer ${
              isDark
                ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                : 'text-red-600 hover:text-red-700 hover:bg-red-50'
            }`}
            title="Clear Scratchpad"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Editor & Preview Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Raw Editor Pane */}
        {(activeTab === 'edit' || activeTab === 'split') && (
          <div className={`flex-1 flex flex-col p-4 ${activeTab === 'split' ? (isDark ? 'border-r border-neutral-800/80' : 'border-r border-slate-200') : ''}`}>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write sovereign markdown notes, checklists, or embed links here..."
              className={`flex-1 w-full font-mono text-xs p-3 rounded-lg border focus:outline-none resize-none leading-relaxed ${
                isDark
                  ? 'bg-neutral-900/40 text-neutral-200 border-neutral-800 focus:border-emerald-500/50'
                  : 'bg-slate-50 text-slate-900 border-slate-200 focus:border-emerald-500'
              }`}
            />
          </div>
        )}

        {/* Rendered Preview Pane */}
        {(activeTab === 'preview' || activeTab === 'split') && (
          <div className={`flex-1 overflow-y-auto p-5 font-sans custom-scrollbar ${
            isDark ? 'bg-neutral-950/40 text-neutral-200' : 'bg-white text-slate-800'
          }`}>
            <div className="max-w-none">
              {renderSimpleMarkdown(content)}
            </div>
          </div>
        )}
      </div>

      {/* Footer Hotkey Guide */}
      <div className={`px-5 py-2.5 border-t flex items-center justify-between text-[11px] font-mono ${
        isDark ? 'bg-neutral-900/60 border-neutral-800/80 text-neutral-400' : 'bg-slate-50 border-slate-200 text-slate-500'
      }`}>
        <span>Toggle shortcut: <kbd className={`px-1 py-0.5 rounded ${isDark ? 'bg-neutral-800 text-neutral-300' : 'bg-slate-200 text-slate-700'}`}>Alt+N</kbd></span>
        <span className={isDark ? 'text-emerald-400/80' : 'text-emerald-600 font-semibold'}>Local Scratchpad • Auto-saved</span>
      </div>
    </div>
  );
}
